import React, { useEffect, useState, lazy } from "react";
import {
  createNativeStackNavigator,
  NativeStackScreenProps,
} from "@react-navigation/native-stack";
import Routes from "@navigations/Routes";
import { toastConfig } from "@components/molecules/VDBToast";
import Toast from "react-native-toast-message";
import { useLinkTo, useNavigation } from "@react-navigation/native";
import { navigationRef } from "@navigations/utils/RootNavigation";
import SplashScreen from "@scenes/splash-screen/SplashScreen";
import { useSplashScreenController } from "@scenes/splash-screen/utils";
import useUserAuthentication from "@scenes/splash-screen/useUserAuthentication";
import { useDispatch, useSelector } from "react-redux";
import { useTheme } from "styled-components/native";
import { DeviceEventEmitter, Linking, Platform } from "react-native";
import { useInitialUrl } from "@utils/useInitialUrl";
import AIChatBotScreen from "@scenes/ai-chatbot/AIChatBotScreen";
import FloatingChatBotButton from "@components/molecules/AI-ChatBot/FloatingChatBotButton";
import { getActiveRouteName } from "@utils/analytics";
import {
  PERSISTENCE_KEY,
  saveHistoryToSessionStorage,
} from "@utils/product/productValidators";
import { useTranslation } from "react-i18next";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { useFirebaseInitialization } from "../hooks/useFirebaseInitialization";
import {
  useAIChatBotConfig,
  useAppSettings,
  useEnvConfig,
} from "@redux/slices/globalSlice";
import { useInjectScript } from "../hooks/useInjectScript";
import { useOrgSettingsSlice } from "@redux/slices/orgSettingsSlice";
import { rewriteDeeplink } from "@utils/rewriteDeeplink";
import { setBuilderProduct, useAppState } from "@redux/slices/appStateSlice";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { chatBotConfig } from "@navigations/utils/constants";
import { useNavigationHistory } from "../hooks/useNavigationHistory";
import { setIsSignoutUrlRedirecting } from "@redux/slices/appStateSlice";
import { ProductCategory } from "@utils/constants";
import { GlobalAdBanner } from "@scenes/searchResults/AdsComponent";
import useExportStatus from "../hooks/useExportStatus";
import {
  CoachOverlayProvider,
  ScopedCopilotProvider,
} from "@components/CoachOverlay";

// Platform-conditional imports: lazy on web, direct on mobile
const TabNavigator =
  Platform.OS === "web"
    ? lazy(() => import("@navigations/tab-navigator"))
    : require("@navigations/tab-navigator").default;

const AuthenticationStack =
  Platform.OS === "web"
    ? lazy(() => import("@navigations/auth-navigator"))
    : require("@navigations/auth-navigator").default;

const GlobalScreens =
  Platform.OS === "web"
    ? lazy(() => import("@navigations/global-navigator"))
    : require("@navigations/global-navigator").default;

const BulkRequestNavigator =
  Platform.OS === "web"
    ? lazy(() => import("./bulk-list-navigator"))
    : require("./bulk-list-navigator").default;

const OnboardingWeb =
  Platform.OS === "web"
    ? lazy(() => import("@scenes/onboarding/OnboardingWeb"))
    : require("@scenes/onboarding/OnboardingWeb").default;

export type RootStackParams = {
  [Routes.AppNavigator]: undefined;
  [Routes.AuthNavigator]: undefined;
  [Routes.ExploreNavigator]: {
    screenName: string;
  };
  [Routes.Splash]: undefined;
  [Routes.FavoritesNavigator]: undefined;
  [Routes.SearchText]: {
    productType: string;
  };
  [Routes.BulkRequestNavigator]: undefined;
  [Routes.SearchResultScreen]: {
    productType: string;
    fromNewFilterScreen: boolean;
    sectionName: string;
    productPrefix: string;
  };
  [Routes.SavedSearch]: undefined;
  [Routes.Explore]: undefined;
  [Routes.FilterSplitScreen]: {
    productType?: string;
    fromResultScreen?: boolean;
    filterSplitStep?: number;
    sectionName?: string;
    searchText?: string;
  };
  [Routes.FilterScreen]: {
    productType: string;
    fromResultScreen: boolean;
  };
  [Routes.PDPScreen]: {
    productID: number;
    productType: string;
  };
  [Routes.RequestModifyScreen]: undefined;
  [Routes.ReviewPage]: {
    productID: number;
    productType: string;
  };
};

export type NativeStackNavigationProps<
  ScreenName extends keyof RootStackParams
> = NativeStackScreenProps<RootStackParams, ScreenName>;

const Stack = createNativeStackNavigator<RootStackParams>();

const getActiveRouteParams = (state: any): Record<string, any> => {
  if (!state?.routes?.length) return {};
  const route = state.routes[state.index ?? 0];
  if (route?.state) return getActiveRouteParams(route.state);
  return route?.params ?? {};
};

const MainStack = ({
  initialScreen,
  initialUrls,
}: {
  initialScreen: Routes | undefined;
  initialUrls: string | undefined;
}) => {
  const navigation = useNavigation();
  const appState = useAppState();
  const dispatch = useDispatch();
  const { getPriceMode } = useUserInfo();
  const { handleBuilderNavigationState } = useNavigationHistory();
  const toastDisplayTime = useSelector(
    (state: any) => state.globalData?.app_settings?.toast_display_time
  );
  const activeProductType = useSelector(
    (state: any) => state.appState?.activeProduct
  );
  const config = useSelector((state: any) => state.globalData?.firebase_config);
  useFirebaseInitialization(config);
  const aiChatBotConfig = useAIChatBotConfig();
  const theme = useTheme();
  const userInfo = useUserInfo();
  const isGuest = useSelector((state: any) => state.userInfo.isGuest);
  const { resetInitialUrl } = useInitialUrl();
  let isReady = useUserAuthentication(initialUrls);
  const { t } = useTranslation();
  const { isLoadingConfigurations } = useSplashScreenController();
  const getAppSettings = useAppSettings();

  const isRestrictedPublicUser =
    isGuest && getAppSettings && getAppSettings?.restrict_public_login;

  isReady =
    isReady &&
    (userInfo?.user
      ? isRestrictedPublicUser
        ? initialUrls?.includes("item-detail") ||
          initialUrls?.includes(Routes.PDPScreen)
        : true
      : false);

  const envConfig = useEnvConfig();
  const { initStatus } = useOrgSettingsSlice();
  const [isRedirecting, setIsRedirecting] = useState(false);
  useExportStatus();
  const isSignoutUrlRedirecting = useSelector(
    (state: any) => state.appState?.isSignoutUrlRedirecting
  );
  useInjectScript(
    initStatus?.web_plugins_script,
    getAppSettings?.isAppLive,
    envConfig
  );
  const linkTo = useLinkTo();
  const excludedRoutes = [
    Routes.HomeScreen,
    Routes.Explore,
    Routes.Favorites,
    Routes.More,
    Routes.OnboardingWeb,
    Routes.Register,
    Routes.PagesScreen,
  ];
  const [navigationState, setNavigationState] = useState<any>(null);
  const [routeName, setRouteName] = useState<string>("Unknown");

  useEffect(() => {
    // Listen on the root navigationRef instead of useNavigation() — `MainStack`
    // sits between NavigationContainer and the first Navigator, so the local
    // navigation prop's "state" event does not fire reliably for nested updates
    // (e.g. after the auth → main switch when filter-screen mounts deeper).
    const updateFromNavigation = () => {
      if (!navigationRef.isReady()) return;
      try {
        const state = navigationRef.getRootState();
        if (state) {
          setNavigationState(state);
          setRouteName(getActiveRouteName(state));
        }
      } catch {
        // Navigation state can be unavailable during initialization.
        console.log("Navigation state not available yet");
      }
    };

    updateFromNavigation();
    const unsubscribe = navigationRef.addListener(
      "state",
      updateFromNavigation
    );
    return unsubscribe;
  }, []);

  const includedProductTypes = [
    ProductCategory.DIAMOND,
    ProductCategory.LAB_GROWN_DIAMOND,
  ];
  const includedRoutes = [
    Routes.FilterScreen,
    Routes.SearchResultScreen,
    Routes.FilterSplitScreen,
  ];
  const currentPriceMode = getPriceMode();
  const isPriceModeEnabled =
    chatBotConfig.floatingButtonVisibility.enabledPriceModes.includes(
      currentPriceMode
    );
  const showChatBotButton =
    userInfo?.user?.feature_settings?.enable_chatbot &&
    isPriceModeEnabled &&
    (isReady &&
    includedProductTypes.includes(activeProductType as ProductCategory) &&
    routeName === Routes.ExploreNavigator &&
    theme.isLargeScreen
      ? includedProductTypes.includes(activeProductType as ProductCategory)
      : includedRoutes.includes(routeName as Routes) &&
        includedProductTypes.includes(activeProductType as ProductCategory));
  const storage = Platform.OS === "web" ? sessionStorage : AsyncStorage;

  useEffect(() => {
    if (initialUrls) {
      if (Platform.OS === "web") {
        try {
          const url = new URL(initialUrls);
          const path = url?.pathname;
          const params = url?.search
            ? new URLSearchParams(url.search)
            : new URLSearchParams();
          if (path !== "/webapp" && path !== "/") {
            const isAllowedDeepLink = excludedRoutes?.every(
              (route) => !path?.includes(route)
            );
            if (
              isReady &&
              isAllowedDeepLink &&
              initialUrls &&
              !/login|auth|register|password|confirmation/.test(path)
            ) {
              const redirectPath = `${path}?${params.toString()}`;
              try {
                linkTo(redirectPath);
                !isRestrictedPublicUser && resetInitialUrl();
              } catch (error) {
                console.log("Failed to navigate to path:", redirectPath);
                console.log("Error details:", error);
              }
            }
          }
        } catch (error) {
          console.error("Invalid URL:", initialUrls);
          console.error("Error details:", error);
        }
      } else {
        if (!isReady) {
          // dispatch(setLoginModalType("default"));
        } else {
          if (
            isReady &&
            initialUrls &&
            (initialUrls?.includes("/webapp") ||
              initialUrls?.includes(process.env.EXPO_PUBLIC_APP_SCHEME + "://"))
          ) {
            let url = initialUrls;
            if (initialUrls?.includes("/webapp")) {
              url = rewriteDeeplink(url);
              url = url.replace(/(^\w+:|^)\/\//, "");
            }
            try {
              Linking.openURL(url);
            } catch (error) {
              console.error("Failed to navigate to path:", url);
              console.error("Error details:", error);
            }
          }
        }
      }
    }
  }, [isReady, initialUrls]);

  useEffect(() => {
    if (isSignoutUrlRedirecting) {
      dispatch(setIsSignoutUrlRedirecting(false));
      setIsRedirecting(true);
    }
  }, [isSignoutUrlRedirecting]);

  useEffect(() => {
    if (
      [Routes.TermsAndConditions, Routes.ForgotPassword].includes(
        initialScreen as keyof RootStackParams
      )
    ) {
      navigation.reset({
        index: 0,
        routes: [
          {
            name: Routes.AuthNavigator,
            state: {
              routes: [{ name: initialScreen }],
            },
          },
        ],
      });
    }
  }, [initialScreen]);

  useEffect(() => {
    if (!navigationState) return;

    saveHistoryToSession();
    const screen = routeName;
    const activeRouteParams = getActiveRouteParams(navigationState);
    const routeParams = {
      ...activeRouteParams,
      productType: activeRouteParams?.productType ?? appState.builderProduct,
    };
    handleBuilderNavigationState(screen, routeParams);
  }, [initialUrls, navigationState]);

  useEffect(() => {
    const subscription = DeviceEventEmitter.addListener(
      "builderProductChange",
      (data) => {
        const screen = data.screen;
        const routeParams = { productType: data.productType };
        dispatch(setBuilderProduct(data.productType));
        handleBuilderNavigationState(screen, routeParams);
      }
    );

    return () => {
      subscription.remove();
    };
  }, []);

  useEffect(() => {
    if (isReady && routeName === Routes.Authentication) {
      setIsRedirecting(true);
    } else {
      setIsRedirecting(false);
    }
  }, [isReady, routeName]);

  const saveHistoryToSession = async () => {
    let _navigationState = await storage.getItem(PERSISTENCE_KEY);
    _navigationState = _navigationState ? JSON.parse(_navigationState) : {};
    const _currentRoute = getActiveRouteName(_navigationState);
    saveHistoryToSessionStorage({ currentRoute: _currentRoute });
  };

  const navigatorContent = (
    <Stack.Navigator
      {...(!isReady && { initialRouteName: Routes.AuthNavigator })}
      screenOptions={{
        headerShown: false,
        headerTitleAlign: "center",
        headerBackButtonDisplayMode: "minimal",
        freezeOnBlur: true,
      }}
    >
      {isReady ? (
        <>
          <Stack.Screen name={Routes.AppNavigator} component={TabNavigator} />
          <Stack.Screen
            name={Routes.BulkRequestNavigator}
            component={BulkRequestNavigator}
          />
          <Stack.Screen
            name={Routes.PostOnBoardingScreen}
            component={OnboardingWeb}
            options={{
              presentation: "fullScreenModal",
              headerShown: false,
              animation: "none",
              orientation: theme.isMobile ? "portrait" : "landscape",
              title: t("onboarding.title.text"),
            }}
          />
          <Stack.Screen
            name={Routes.AIChatBotScreen}
            component={AIChatBotScreen}
            options={{
              presentation: "fullScreenModal",
              animation: "slide_from_bottom",
            }}
          />
          <Stack.Screen
            options={(props: any) => {
              return {
                presentation:
                  props?.route?.params?.params?.presentation || "card",
              };
            }}
            name={Routes.GlobalNavigator}
            component={GlobalScreens}
          />
        </>
      ) : (
        <Stack.Screen
          name={Routes.AuthNavigator}
          component={AuthenticationStack}
        />
      )}
    </Stack.Navigator>
  );

  return (
    <CoachOverlayProvider>
      {navigatorContent}
      <Toast
        config={toastConfig}
        visibilityTime={toastDisplayTime ? Number(toastDisplayTime) : 5000}
      />
      <SplashScreen isVisible={isLoadingConfigurations || isRedirecting} />
      <GlobalAdBanner />
      <ScopedCopilotProvider scope="AISearchWidget">
        {showChatBotButton &&
          (theme.isMobile ||
            !(
              aiChatBotConfig?.show_ai_chat_on_filter_split_screen ?? false
            )) && (
            <FloatingChatBotButton
              isDraggable={!theme.isLargeScreen}
              routeName={routeName}
            />
          )}
      </ScopedCopilotProvider>
    </CoachOverlayProvider>
  );
};

export default MainStack;
